import { useEffect, useRef } from 'react'
import { usePrefersReducedMotion } from './usePrefersReducedMotion'

/**
 * Writes --p (0..1, entry-to-exit) and --pin (0..1, scroll-through for tall
 * sections) as CSS custom properties on ref.current. Single rAF loop,
 * intersection-observer gated for performance — mirrors the template's engine.
 *
 * @param {React.RefObject} ref - section element ref
 * @param {{ rootMargin?: string }} options
 */
export function useScrollProgress(ref, options = {}) {
    const reducedMotion = usePrefersReducedMotion()
    const rafRef = useRef(null)
    const activeRef = useRef(false)

    useEffect(() => {
        const el = ref.current
        if (!el) return

        if (reducedMotion) {
            el.style.setProperty('--p', '0')
            el.style.setProperty('--pin', '0')
            return
        }

        const { rootMargin = '60% 0% 60% 0%' } = options

        const obs = new IntersectionObserver(
            ([entry]) => { activeRef.current = entry.isIntersecting },
            { threshold: 0, rootMargin }
        )
        obs.observe(el)

        function tick() {
            if (activeRef.current) {
                const vh = window.innerHeight
                const r = el.getBoundingClientRect()
                const total = r.height + vh
                const traveled = vh - r.top
                const p = Math.max(0, Math.min(1, traveled / total))
                el.style.setProperty('--p', p.toFixed(4))

                if (r.height > vh + 4) {
                    const pin = Math.max(0, Math.min(1, -r.top / (r.height - vh)))
                    el.style.setProperty('--pin', pin.toFixed(4))
                }
            }
            rafRef.current = requestAnimationFrame(tick)
        }
        rafRef.current = requestAnimationFrame(tick)

        return () => {
            obs.disconnect()
            if (rafRef.current) cancelAnimationFrame(rafRef.current)
        }
    }, [ref, reducedMotion, options])
}
